import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { fmtBytes } from "../lib/format";
import { monthKey } from "../lib/csvAnalysis";

const ColSelect = ({ label, value, onChange, headers, testId, optional, noneLabel = "— None (count only) —" }) => (
  <div>
    <p className="label-mono mb-2">{label}</p>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="bg-[#0B0F19] border-slate-700 text-slate-100"><SelectValue placeholder="Select column" /></SelectTrigger>
      <SelectContent>
        {optional && <SelectItem value="none">{noneLabel}</SelectItem>}
        {headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export const ColumnMapping = ({ file, preview, mapping, setMapping, onConfirm, onCancel }) => {
  const set = (k) => (v) => setMapping({ ...mapping, [k]: v });
  const sampleMonth = mapping.date !== "" ? monthKey(preview.rows[0]?.[+mapping.date], mapping.dateFormat) : null;
  const ready = mapping.user !== "" && mapping.date !== "" && mapping.user !== mapping.date;
  return (
    <div className="max-w-6xl mx-auto px-6 py-14 fade-up" data-testid="column-mapping-panel">
      <p className="label-mono mb-3">Step 2 · Confirm columns</p>
      <h2 className="font-display text-3xl font-bold text-slate-100">{file.name}</h2>
      <p className="text-slate-500 text-sm mt-1 font-mono">{fmtBytes(file.size)} · {preview.headers.length} columns detected</p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-5 panel p-6">
        <ColSelect label="User column" value={mapping.user} onChange={set("user")} headers={preview.headers} testId="user-column-select" />
        <ColSelect label="Date column" value={mapping.date} onChange={set("date")} headers={preview.headers} testId="date-column-select" />
        <div>
          <p className="label-mono mb-2">Date format</p>
          <Select value={mapping.dateFormat} onValueChange={set("dateFormat")}>
            <SelectTrigger data-testid="date-format-select" className="bg-[#0B0F19] border-slate-700 text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="MDY">MM/DD/YYYY (or ISO)</SelectItem>
              <SelectItem value="DMY">DD/MM/YYYY (or ISO)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs mt-2 font-mono text-slate-500" data-testid="date-sample-result">First row → {sampleMonth || "unparsed"}</p>
        </div>
        <ColSelect label="Quantity picked" value={mapping.qty} onChange={set("qty")} headers={preview.headers} testId="qty-column-select" optional />
        <div>
          <p className="label-mono mb-2">Pick time</p>
          <Select value={mapping.time} onValueChange={set("time")}>
            <SelectTrigger data-testid="time-column-select" className="bg-[#0B0F19] border-slate-700 text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— None —</SelectItem>
              <SelectItem value="date">From date column</SelectItem>
              {preview.headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <ColSelect label="Hoptime (picks/hr)" value={mapping.hop} onChange={set("hop")} headers={preview.headers} testId="hop-column-select" optional noneLabel="— None —" />
      </div>
      <div className="mt-8 panel overflow-x-auto">
        <table className="w-full text-sm" data-testid="mapping-preview-table">
          <thead><tr>{preview.headers.map((h, i) => (
            <th key={i} className={`text-left px-4 py-3 font-mono text-xs uppercase tracking-wider whitespace-nowrap border-b border-slate-800 ${[mapping.user, mapping.date, mapping.qty, mapping.hop, mapping.time].includes(String(i)) ? "text-ep-soft" : "text-slate-500"}`}>{h}</th>
          ))}</tr></thead>
          <tbody>{preview.rows.slice(0, 6).map((r, ri) => (
            <tr key={ri} className="border-b border-slate-800/60 last:border-0">{preview.headers.map((_, i) => <td key={i} className="px-4 py-2.5 text-slate-300 whitespace-nowrap font-mono text-xs">{r[i]}</td>)}</tr>
          ))}</tbody>
        </table>
      </div>
      <div className="mt-8 flex gap-3">
        <Button data-testid="confirm-column-mapping-button" disabled={!ready} onClick={onConfirm} className="bg-ep hover:bg-ep-hover text-white">Analyse file</Button>
        <Button data-testid="cancel-mapping-button" variant="ghost" onClick={onCancel} className="text-slate-400 hover:text-white hover:bg-slate-800">Choose another file</Button>
      </div>
    </div>
  );
};
